import { supabase } from '../lib/supabase';
import { isDemoMode } from '../lib/demo';
import { demoStore } from '../demo/store';
import type { NotifPrefs, UserId } from '../domain/types';

/* eslint-disable @typescript-eslint/no-explicit-any */

/** اگه کاربر هنوز ردیفی نداره، همین‌ها اعمال می‌شن (هم‌راستا با پیش‌فرضِ دیتابیس). */
export const DEFAULT_PREFS: NotifPrefs = {
  friend_request: 'push',
  signal: 'push',
  event: 'inapp',
  // کسی که خودش تگ دنبال کرده منتظرِ خبرشه — برای همین push و نه inapp.
  tag_follow: 'push',
  indicator: 'dot',
  auto_update: true,
};

export const notifPrefsRepo = {
  async mine(): Promise<NotifPrefs> {
    if (isDemoMode) return demoStore.notifPrefs.mine();
    const { data, error } = await supabase
      .from('notification_prefs').select('*').maybeSingle();
    if (error) throw error;
    if (!data) return { ...DEFAULT_PREFS };
    return {
      friend_request: data.friend_request,
      signal: data.signal,
      event: data.event,
      tag_follow: data.tag_follow,
      indicator: data.indicator,
      auto_update: data.auto_update,
    };
  },

  /**
   * یه فیلد رو عوض می‌کنه. upsert چون ردیف تا اولین تغییر اصلاً وجود نداره،
   * و بقیه‌ی فیلدها باید با مقدار فعلی نوشته شن وگرنه به پیش‌فرض برمی‌گردن.
   */
  async patch(uid: UserId, patch: Partial<NotifPrefs>): Promise<void> {
    if (isDemoMode) return demoStore.notifPrefs.patch(patch);
    const current = await this.mine();
    const { error } = await supabase.from('notification_prefs').upsert({
      user_id: uid,
      ...current,
      ...patch,
      updated_at: new Date().toISOString(),
    });
    if (error) throw error;
  },

  /**
   * توکن پوشِ این دستگاه. با هر بار باز شدن اپ تازه میشه.
   *
   * از راهِ RPC، نه `upsert`ِ مستقیم — چون توکن شناسه‌ی **دستگاه** است نه
   * حساب، و ثبتش یعنی «این گوشی حالا دستِ من است». ادعای حسابِ قبلی روی
   * همان دستگاه باید برداشته شود، و RLS (به‌درستی) نمی‌گذارد کاربر ردیفِ
   * کسِ دیگری را دست بزند.
   *
   * بدونِ این، ردیف‌ها روی هم می‌ماندند و **اعلانِ هر کدام از آن حساب‌ها
   * روی همین یک گوشی می‌نشست**: «سیگنالم را لغو کردم و پوشش برای خودم
   * آمد» — که در واقع پوشِ گیرنده بود روی گوشیِ سازنده. مایگریشن 0123.
   */
  async saveToken(_uid: UserId, token: string, platform: string): Promise<void> {
    if (isDemoMode) return demoStore.notifPrefs.saveToken();
    const { error } = await supabase.rpc('claim_push_token', {
      p_token: token, p_platform: platform,
    });
    if (error) throw error;
  },

  /**
   * خاموش کردنِ اعلانِ یک دستگاه = حذفِ ردیفش — نه فقط بی‌صدایی.
   *
   * ⚠ تا وقتی ردیف هست، فرستنده (`notify-push`) برای همین دستگاه تلاش
   * می‌کند؛ «خاموش»ی که فقط UI را عوض کند دروغ است. RLS خودش حذف را به
   * ردیف‌های خودِ کاربر محدود می‌کند (سیاستِ `pt_all`، مایگریشن 0008).
   */
  async deleteToken(token: string): Promise<void> {
    if (isDemoMode) return demoStore.notifPrefs.deleteToken();
    const { error } = await supabase.from('push_tokens').delete().eq('token', token);
    if (error) throw error;
  },

  /**
   * «این حساب اپِ گوشی را دارد؟»
   *
   * تنها مصرفش تصمیمِ `shouldOfferPush` است: کسی که اپ را نصب کرده نباید
   * برای اعلانِ مرورگر پرسیده شود — راهِ بهترش را همین حالا دارد.
   *
   * ⚠ نبودِ ردیف «قطعاً نصب ندارد» نیست، «نشانه‌ای نداریم» است — مثلاً
   * کسی که اپ را دارد ولی اجازه‌ی اعلان نداده. نتیجه‌اش حداکثر یک کارتِ
   * اضافه است که با «نه» بسته می‌شود؛ خطای بی‌ضرر در جهتِ درست.
   */
  async hasNativeToken(uid: UserId): Promise<boolean> {
    if (isDemoMode) return false;
    const { data } = await supabase
      .from('push_tokens').select('token')
      .eq('user_id', uid).in('platform', ['android', 'ios']).limit(1);
    return !!data?.length;
  },

  /**
   * «اولین قدمِ واقعی‌اش را برداشته؟» — سیگنالی ساخته، درخواستِ دوستی
   * فرستاده، یا دوستی‌ای را قبول کرده.
   *
   * تنها مصرفش `shouldOfferPush` است (قرارِ WHOSUP-71، تصمیمِ ۲): کارتِ
   * پیشنهادِ اعلانِ مرورگر نباید سرِ اولین ورود بیاید، بلکه وقتی که کاربر
   * چیزی برای خبردار شدن دارد.
   *
   * ⚠ درخواستِ دوستیِ *دریافتی* که هنوز قبول نشده عمداً نمی‌شمرد — گیرنده
   * هنوز خودش قدمی برنداشته؛ «افزودن» و «قبول» در قرار، هر دو فعلِ خودِ
   * کاربرند.
   */
  async hasFirstActivity(uid: UserId): Promise<boolean> {
    if (isDemoMode) return true;
    const { data: sig } = await supabase
      .from('signals').select('id').eq('creator_id', uid).limit(1);
    if (sig?.length) return true;
    const { data: fr } = await supabase
      .from('friendships').select('id')
      .or(`requester_id.eq.${uid},and(addressee_id.eq.${uid},status.eq.accepted)`)
      .limit(1);
    return !!fr?.length;
  },
};
